'use client';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe, type StripeElements, type StripeElementsOptions } from '@stripe/stripe-js';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import ErrorLabel from '@cineamo/ui/src/label/ErrorLabel';
import Modal from '@cineamo/ui/src/modals/Modal';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import type { BuyerDetails } from './actions';
import {
    paymentIntentIdOf,
    reportBookingError,
    trackBookingEvent,
    trackPaymentConfirmed
} from './booking-telemetry-client';
import { mockWallet } from './MockWalletButton';
import {
    type PendingRedirect,
    readPendingRedirect,
    type RedirectRecheck,
    redirectRecheck,
    writePendingRedirect
} from './redirect-recheck';
import { createSingleFlight } from './single-flight';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '');
const googleFontsUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700';

const INPUT_CLASS_NAME =
    'w-full h-[48px] bg-[var(--input-background-color)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--focus-ring-color)] text-[var(--input-text-color)] text-body-16 rounded-[4px] px-[16px] border-none placeholder:text-[var(--input-placeholder-color)]';

type BuyerField = 'email' | 'firstName' | 'lastName';
const BUYER_FIELDS: BuyerField[] = ['email', 'firstName', 'lastName'];
const FIELD_IDS: Record<BuyerField, string> = {
    email: 'checkout-email',
    firstName: 'checkout-first-name',
    lastName: 'checkout-last-name'
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** This tab only (sessionStorage), and gone once paid: what the guest typed, kept across a payment redirect. */
const STORED_BUYER_KEY = 'checkout-buyer';

function readStoredBuyer(): BuyerDetails | null {
    try {
        const stored = window.sessionStorage.getItem(STORED_BUYER_KEY);
        return stored ? (JSON.parse(stored) as BuyerDetails) : null;
    } catch {
        return null;
    }
}

export function storeBuyer(buyer: BuyerDetails | null) {
    try {
        if (buyer) window.sessionStorage.setItem(STORED_BUYER_KEY, JSON.stringify(buyer));
        else window.sessionStorage.removeItem(STORED_BUYER_KEY);
    } catch {
        // Private mode or blocked storage: the guest types it again.
    }
}

/** How often a tab whose payment left for its provider asks Stripe what became of it, besides on return. */
const REDIRECT_RECHECK_MS = 15_000;

/** The provider a redirecting method goes to, by name; null for one without a name of its own (a bank's page). */
function providerName(method: string | null | undefined): string | null {
    switch (method) {
        case 'paypal':
            return 'PayPal';
        case 'amazon_pay':
            return 'Amazon Pay';
        case 'wero':
            return 'Wero';
        default:
            return null;
    }
}

function sessionStore(): Storage | undefined {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

/** The pay button's words for the chosen method; the footer and the sheet say the same. */
export function payButtonLabel(
    t: (key: string, values?: Record<string, string>) => string,
    method: string | null | undefined,
    amount: string
): string {
    switch (method) {
        case 'card':
            return t('checkout.pay-with-card', { amount });
        case 'paypal':
        case 'amazon_pay':
        case 'wero':
            return t('checkout.continue-to', { method: providerName(method) ?? '' });
        default:
            return t('pay-now', { amount });
    }
}

type StripeBridge = { stripe: Stripe | null; elements: StripeElements | null };

/** Hands the Elements group's instances up to the form, which also renders without one (nothing due). */
function StripeBridgeSetter({ onChange }: { onChange: (bridge: StripeBridge) => void }): null {
    const stripe = useStripe();
    const elements = useElements();
    useEffect(() => onChange({ stripe, elements }), [onChange, stripe, elements]);
    return null;
}

function elementsOptions(
    amount: number,
    currency: string,
    websiteStyle: WebsiteStyle & WebsiteInputStyle
): StripeElementsOptions {
    return {
        // Deferred intent: the PaymentIntent is created on "Pay", with the buyer's details, and captures manually.
        mode: 'payment',
        amount,
        currency: currency.toLowerCase(),
        captureMethod: 'manual',
        fonts: [{ cssSrc: googleFontsUrl }],
        appearance: {
            variables: {
                colorPrimary: websiteStyle?.primaryColor ?? undefined,
                colorBackground: websiteStyle?.backgroundColor ?? undefined,
                colorText: websiteStyle?.onBackgroundColor ?? undefined,
                fontFamily: 'Montserrat, sans-serif',
                borderRadius: '0px'
            },
            rules: {
                '.Input': {
                    backgroundColor: websiteStyle?.inputBackgroundColor ?? '',
                    color: websiteStyle?.inputTextColor ?? '',
                    borderRadius: '4px',
                    border: 'none',
                    boxShadow: 'none'
                },
                '.Input:focus': {
                    outline: 'none',
                    boxShadow: `inset 0 0 0 2px ${websiteStyle?.primaryColor}`
                },
                '.Input::placeholder': {
                    color: websiteStyle?.inputPlaceholderColor ?? ''
                },
                '.Label': {
                    color: withOpacity(websiteStyle?.onBackgroundColor ?? '', 0.5) ?? '',
                    letterSpacing: '0.3px',
                    fontSize: '12px',
                    fontWeight: '500'
                },
                '.AccordionItem': {
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderBottom: 'none',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    backgroundColor: 'transparent',
                    borderTopColor: websiteStyle?.inputBackgroundColor ?? ''
                }
            }
        }
    };
}

type CheckoutFormProps = {
    /** What Stripe charges, in cents; 0 when gift cards cover everything, which needs no payment details. */
    amount: number;
    currency: string;
    websiteStyle: WebsiteStyle & WebsiteInputStyle;
    /** Bumped by the footer's pay button. */
    submitTrigger: number;
    /** The signed-in account's details; complete ones show as one line instead of the fields. */
    defaultBuyer?: Partial<BuyerDetails>;
    requestPaymentIntent: (buyer: BuyerDetails, onEmailRefused?: () => void) => Promise<string | 'paid' | null>;
    onPaymentComplete: () => void;
    /** `'wallet'` or a Payment Element type (`card`, `paypal`, ...); null before a choice. The footer names it. */
    selectedMethod: string | null;
    onSelectMethod: (method: string | null) => void;
    /** Shown where a payment error would be, e.g. why the guest is back after a cancelled PayPal payment. */
    notice?: string;
};

/**
 * The checkout's payment part (CIN-15367): the payment methods as tabs, and "Ticket-Versand" (email, name) as a
 * modal from the pay button. Apple Pay and Google Pay skip it, the footer's wallet button brings both; card, Amazon
 * Pay, Wero and PayPal need ours (a card carries no email, and Stripe hands over Amazon Pay's not at all and PayPal's
 * not reliably). A signed-in account with both skips it too. One page; no separate payment step.
 */
export default function CheckoutForm(props: CheckoutFormProps): React.JSX.Element {
    const { amount, currency, websiteStyle } = props;
    const bridge = useRef<StripeBridge>({ stripe: null, elements: null });
    const setBridge = useCallback((next: StripeBridge) => {
        bridge.current = next;
    }, []);
    const form = (
        <CheckoutFormFields
            {...props}
            bridge={bridge}
        />
    );
    if (amount === 0) return form;
    return (
        <Elements
            stripe={stripePromise}
            options={elementsOptions(amount, currency, websiteStyle)}>
            <StripeBridgeSetter onChange={setBridge} />
            {form}
        </Elements>
    );
}

function CheckoutFormFields({
    amount,
    websiteStyle,
    submitTrigger,
    defaultBuyer,
    requestPaymentIntent,
    onPaymentComplete,
    selectedMethod,
    onSelectMethod,
    notice,
    bridge
}: CheckoutFormProps & { bridge: { current: StripeBridge } }) {
    const t = useTranslations('tickets');
    const { track } = useAnalytics();
    const { isProcessing, setIsProcessing } = useCartProvider();

    const hasCompleteAccount = !!(defaultBuyer?.email && defaultBuyer?.firstName && defaultBuyer?.lastName);
    // "Ticket-Versand" opens over the page from the pay button, so the page itself is only order and payment method.
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const formatter = useFormat();
    const { currency, cartUuid } = useCartProvider();
    // Back from a failed redirect, the guest finds what they typed before leaving.
    const [storedBuyer] = useState(readStoredBuyer);
    const [email, setEmail] = useState(storedBuyer?.email ?? defaultBuyer?.email ?? '');
    const [firstName, setFirstName] = useState(storedBuyer?.firstName ?? defaultBuyer?.firstName ?? '');
    const [lastName, setLastName] = useState(storedBuyer?.lastName ?? defaultBuyer?.lastName ?? '');
    // Shown under each field; the browser's own bubbles would point behind the fixed footer on small screens.
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<BuyerField, string>>>({});
    const [message, setMessage] = useState<string>();
    const currentPaymentMethod = useRef<string | null>(null);
    const isMockWallet = useRef(!!mockWallet());
    // Set synchronously, unlike `isProcessing`: a double click or Enter right after a click arrives before the
    // re-render, and each pass would create its own PaymentIntent (CIN-15436).
    const paying = useRef(createSingleFlight());
    const submitting = useRef(createSingleFlight());
    // A payment that left for its provider and did not come back through the return URL (CIN-15446).
    const [stalled, setStalled] = useState<PendingRedirect | null>(null);
    // Only a tab the buyer left (app switch, another tab, bfcache) rechecks: while a 3-D Secure modal is open the
    // PaymentIntent is `requires_action` too. A pending payment from before a reload has certainly left.
    const leftSincePayment = useRef(readPendingRedirect(sessionStore()) !== null);
    const reportedStall = useRef<string | null>(null);

    const validate = useCallback((): Partial<Record<BuyerField, string>> => {
        const required = t('checkout.required');
        return {
            email: !email.trim()
                ? required
                : EMAIL_PATTERN.test(email.trim())
                  ? undefined
                  : t('checkout.invalid-email'),
            // CIN-15320: both name parts are obligatory, and whitespace is not a name.
            firstName: firstName.trim() ? undefined : required,
            lastName: lastName.trim() ? undefined : required
        };
    }, [email, firstName, lastName, t]);

    const pay = useCallback(
        async (buyer: BuyerDetails) => {
            if (!paying.current.enter()) return;
            const { stripe, elements } = bridge.current;
            setMessage(undefined);
            setIsProcessing(true);
            trackBookingEvent('booking_payment_submitted', { method: selectedMethod ?? 'card', amount_due: amount });
            try {
                const result = await requestPaymentIntent(buyer, () => {
                    // Refused address: the sheet stays open with the reason at the field.
                    setFieldErrors({ email: t('payment.email-input.email-undeliverable') });
                    document.getElementById(FIELD_IDS.email)?.focus();
                });
                if (result === null) return;
                if (result === 'paid') {
                    trackBookingEvent('booking_payment_confirmed', { outcome: 'paid', method: selectedMethod });
                    setIsSheetOpen(false);
                    storeBuyer(null);
                    onPaymentComplete();
                    return;
                }
                // Something is due but Stripe is not there to take it: never report a payment that did not happen.
                if (!stripe || !elements) {
                    reportBookingError({ step: 'confirm-payment', code: 'stripe-not-loaded' });
                    showToast({ toastType: toast.error, messageTitle: t('error.something-went-wrong') });
                    return;
                }
                setIsSheetOpen(false);
                storeBuyer(buyer);
                leftSincePayment.current = false;
                writePendingRedirect(sessionStore(), { clientSecret: result, method: selectedMethod, cartUuid });
                const { error } = await stripe.confirmPayment({
                    elements,
                    clientSecret: result,
                    confirmParams: {
                        // Redirecting methods (PayPal, Amazon Pay, Wero) come back here; the page reads the result.
                        return_url: window.location.origin + window.location.pathname,
                        // The Payment Element asks for neither: they come from the sheet.
                        payment_method_data: {
                            billing_details: { name: `${buyer.firstName} ${buyer.lastName}`, email: buyer.email }
                        }
                    },
                    redirect: 'if_required'
                });
                // Answered in this tab, no redirect: nothing left to recheck.
                writePendingRedirect(sessionStore(), null);
                trackPaymentConfirmed(selectedMethod, error);
                if (error) {
                    // Card and input errors show at the field inside the Payment Element; the rest gets a line.
                    if (error.type !== 'card_error' && error.type !== 'validation_error') setMessage(error.message);
                    document
                        .getElementById('checkout-payment')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                }
                // Authorized (manual capture): the webhook captures and fulfills, the success view follows the order.
                storeBuyer(null);
                onPaymentComplete();
            } catch (error) {
                // Nothing here is expected to throw: Stripe's promise rejecting, or the payment-intent request
                // outside its own handling. The buyer must not be left on a spinner with nobody told (CIN-15436).
                reportBookingError({ step: 'confirm-payment', code: 'unexpected', error });
                showToast({ toastType: toast.error, messageTitle: t('error.something-went-wrong') });
            } finally {
                paying.current.leave();
                setIsProcessing(false);
            }
        },
        [amount, bridge, cartUuid, onPaymentComplete, requestPaymentIntent, selectedMethod, setIsProcessing, t]
    );

    /** Acts on what Stripe says about a payment that left this tab; returns what it decided. */
    const applyRecheck = useCallback(
        (pending: PendingRedirect, status: string | undefined): RedirectRecheck => {
            const outcome = redirectRecheck(status);
            if (outcome === 'wait') return outcome;
            const context = { status, method: pending.method, payment_intent: paymentIntentIdOf(pending.clientSecret) };
            // The pay() that started it never returns from a redirect that did not happen: end it here.
            paying.current.leave();
            setIsProcessing(false);
            if (outcome === 'stalled') {
                setStalled(pending);
            } else {
                writePendingRedirect(sessionStore(), null);
                setStalled(null);
            }
            if (outcome === 'complete') {
                // Like a return through the redirect: the success view follows the order from here.
                trackBookingEvent('booking_redirect_returned', { ...context, via: 'recheck' });
                trackBookingEvent('booking_payment_confirmed', { ...context, outcome: 'authorized', via: 'recheck' });
                storeBuyer(null);
                onPaymentComplete();
                return outcome;
            }
            if (reportedStall.current !== `${context.payment_intent}:${outcome}`) {
                reportedStall.current = `${context.payment_intent}:${outcome}`;
                trackBookingEvent('booking_redirect_stalled', { ...context, outcome });
            }
            if (outcome === 'canceled') setMessage(t('checkout.redirect-canceled'));
            return outcome;
        },
        [onPaymentComplete, setIsProcessing, t]
    );

    useEffect(() => {
        const recheck = async () => {
            const pending = readPendingRedirect(sessionStore());
            const stripe = bridge.current.stripe;
            if (!pending || pending.cartUuid !== cartUuid || !stripe || !leftSincePayment.current) return;
            try {
                const { paymentIntent } = await stripe.retrievePaymentIntent(pending.clientSecret);
                applyRecheck(pending, paymentIntent?.status);
            } catch {
                // Offline for a moment: the next return, focus or interval asks again.
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') leftSincePayment.current = true;
            else void recheck();
        };
        const onPageShow = (event: PageTransitionEvent) => {
            if (!event.persisted) return;
            leftSincePayment.current = true;
            void recheck();
        };
        const onFocus = () => void recheck();
        const interval = setInterval(() => void recheck(), REDIRECT_RECHECK_MS);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pageshow', onPageShow);
        window.addEventListener('focus', onFocus);
        void recheck();
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pageshow', onPageShow);
            window.removeEventListener('focus', onFocus);
        };
    }, [applyRecheck, bridge, cartUuid]);

    /** "Weiter zu PayPal": the same PaymentIntent, back to where its confirmation stopped. */
    const continueAtProvider = useCallback(async () => {
        const stripe = bridge.current.stripe;
        if (!stalled || !stripe || !paying.current.enter()) return;
        setStalled(null);
        setMessage(undefined);
        setIsProcessing(true);
        leftSincePayment.current = false;
        let outcome: RedirectRecheck = 'wait';
        try {
            // May leave the tab for the provider again, like the first confirmation.
            const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: stalled.clientSecret });
            if (error) {
                writePendingRedirect(sessionStore(), null);
                trackPaymentConfirmed(stalled.method, error);
                setMessage(error.message);
                return;
            }
            outcome = applyRecheck(stalled, paymentIntent?.status);
        } catch (error) {
            reportBookingError({ step: 'confirm-payment', code: 'next-action', error });
            setMessage(t('error.something-went-wrong'));
        } finally {
            if (outcome === 'wait') {
                paying.current.leave();
                setIsProcessing(false);
            }
        }
    }, [applyRecheck, bridge, setIsProcessing, stalled, t]);

    /** "Anderes Zahlungsmittel wählen": the open payment is left as it is, the next "Pay" starts over. */
    const chooseAnotherMethod = useCallback(() => {
        writePendingRedirect(sessionStore(), null);
        setStalled(null);
        document.getElementById('checkout-payment')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    /** The footer's pay button: the payment details first, then who gets the tickets (the sheet). */
    const submit = useCallback(async () => {
        if (submitting.current.busy || paying.current.busy) return;
        const { stripe, elements } = bridge.current;
        if (amount > 0 && (!stripe || !elements)) {
            reportBookingError({ step: 'submit', code: 'stripe-not-loaded', level: 'warning' });
            return;
        }
        submitting.current.enter();
        try {
            setMessage(undefined);
            // An incomplete card is marked in place before anything else opens over it.
            if (elements) {
                const { error } = await elements.submit();
                if (error) {
                    document
                        .getElementById('checkout-payment')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                }
            }
            // A signed-in account that knows email and name has nothing to ask.
            if (hasCompleteAccount && defaultBuyer?.email && defaultBuyer.firstName && defaultBuyer.lastName) {
                await pay({
                    email: defaultBuyer.email,
                    firstName: defaultBuyer.firstName,
                    lastName: defaultBuyer.lastName
                });
                return;
            }
            setFieldErrors({});
            setIsSheetOpen(true);
        } finally {
            submitting.current.leave();
        }
    }, [amount, bridge, defaultBuyer, hasCompleteAccount, pay]);

    const confirmSheet = () => {
        if (paying.current.busy) return;
        const errors = validate();
        setFieldErrors(errors);
        const firstInvalid = BUYER_FIELDS.find((field) => errors[field]);
        if (firstInvalid) {
            document.getElementById(FIELD_IDS[firstInvalid])?.focus();
            return;
        }
        void pay({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
    };

    const submitAttempt = useRef(submitTrigger);
    useEffect(() => {
        if (submitTrigger > submitAttempt.current && !isProcessing) {
            submitAttempt.current = submitTrigger;

            void submit();
        }
    }, [submitTrigger, submit, isProcessing]);

    const field = (
        name: BuyerField,
        label: string,
        value: string,
        setValue: (value: string) => void,
        input: React.InputHTMLAttributes<HTMLInputElement>
    ) => {
        const error = fieldErrors[name];
        return (
            <div className="flex min-w-0 flex-1 flex-col space-y-4">
                <label
                    htmlFor={FIELD_IDS[name]}
                    className="text-body-12 opacity-50">
                    {label}
                </label>
                <input
                    {...input}
                    id={FIELD_IDS[name]}
                    required
                    className={cn(INPUT_CLASS_NAME, error && 'ring-2 ring-inset ring-red')}
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setFieldErrors((errors) => ({ ...errors, [name]: undefined }));
                    }}
                    aria-invalid={!!error}
                    aria-describedby={error ? `${FIELD_IDS[name]}-error` : undefined}
                />
                {error && (
                    <p
                        id={`${FIELD_IDS[name]}-error`}
                        className="text-body-12 text-red">
                        {error}
                    </p>
                )}
            </div>
        );
    };

    const shownError = message ?? notice;

    return (
        <div
            id="checkout-form"
            className="mx-auto flex w-full max-w-[680px] flex-col space-y-24 px-[9px] py-16 lg:px-32"
            style={
                {
                    '--input-background-color': websiteStyle?.inputBackgroundColor,
                    '--input-text-color': websiteStyle?.inputTextColor,
                    '--input-placeholder-color': websiteStyle?.inputPlaceholderColor
                } as React.CSSProperties
            }>
            {amount > 0 && (
                <section
                    id="checkout-payment"
                    className="flex flex-col space-y-12">
                    <h3 className="text-body-bold-16">{t('payment.payment-method.title')}</h3>
                    {/* At the top of the section the page scrolls to when paying fails, so it is in view. */}
                    {shownError && <ErrorLabel error={shownError} />}
                    {stalled && (
                        <StalledPayment
                            provider={providerName(stalled.method)}
                            onContinue={() => void continueAtProvider()}
                            onChooseAnother={chooseAnotherMethod}
                        />
                    )}
                    {/* Many guests do not know their new bank card (Visa/Mastercard debit) pays online, and take
                        PayPal, which costs the most. Shown with the card tab open too: on desktop it is the first tab. */}
                    <p className="flex items-start gap-8 text-body-14">
                        <IconInfo16
                            fill="currentColor"
                            className="mt-2 shrink-0"
                        />
                        <span>
                            <span className="text-body-bold-14">
                                {t('payment.payment-method.debit-card-hint-title')}
                            </span>{' '}
                            {t('payment.payment-method.debit-card-hint')}
                        </span>
                    </p>
                    <PaymentElement
                        options={{
                            // Tabs: every method in view at once, without scrolling past an open card form.
                            layout: { type: 'tabs' },
                            // Express first: the wallet tab leads and is preselected where the device has one.
                            // PayPal last, it costs the most.
                            paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'amazon_pay', 'wero', 'paypal'],
                            fields: { billingDetails: { name: 'never', email: 'never' } }
                        }}
                        onChange={(e) => {
                            // Dev mock (see `mockWallet`): Stripe cannot show the wallet tab on a local domain and
                            // reports its first tab, the card, on load; on a real device that first tab is the wallet.
                            if (isMockWallet.current) {
                                isMockWallet.current = false;
                                return;
                            }
                            // The wallet tab is paid for by the footer's Apple Pay / Google Pay button, which brings
                            // email and name; the tab itself only chooses it.
                            const isWalletTab = e.value.type === 'apple_pay' || e.value.type === 'google_pay';
                            onSelectMethod(isWalletTab ? 'wallet' : e.value.type);
                            if (currentPaymentMethod.current !== e.value.type) {
                                currentPaymentMethod.current = e.value.type;
                                track('Payment Method Changed', { paymentMethod: e.value.type });
                            }
                        }}
                    />
                </section>
            )}

            {isSheetOpen && (
                <Modal
                    modalIsOpen={isSheetOpen}
                    setModalIsOpen={setIsSheetOpen}
                    onClose={isProcessing ? undefined : () => setIsSheetOpen(false)}
                    title={t('payment.email-input.title')}
                    className="h-min lg:max-w-[480px]"
                    footer={
                        <button
                            type="button"
                            onClick={confirmSheet}
                            disabled={isProcessing}
                            className={cn(
                                'mt-16 flex min-h-48 w-full items-center justify-center gap-8 rounded-4 px-16 text-button-16 outline-none',
                                'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                                'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2',
                                'disabled:cursor-not-allowed disabled:opacity-50'
                            )}>
                            {isProcessing && <IconLoading16 className="animate-spin" />}
                            {payButtonLabel(t, selectedMethod, formatter.number.formatCurrency(amount / 100, currency))}
                        </button>
                    }>
                    <div
                        className="flex flex-col space-y-12"
                        style={
                            {
                                // The modal is a surface: its own input colors, else the surface's text at 10 %, as in the
                                // snack modal (the page's input color can equal the surface and hide the fields).
                                '--input-background-color':
                                    websiteStyle?.inputBackgroundColorOnSurface ||
                                    'rgba(var(--on-non-transparent-surface-color-rgb),0.1)',
                                '--input-text-color':
                                    websiteStyle?.inputTextColorOnSurface || 'var(--on-non-transparent-surface-color)',
                                '--input-placeholder-color':
                                    websiteStyle?.inputPlaceholderColorOnSurface ||
                                    'rgba(var(--on-non-transparent-surface-color-rgb),0.5)'
                            } as React.CSSProperties
                        }>
                        <p className="text-body-14">{t('payment.email-input.explanation')}</p>
                        {field('email', t('payment.email-input.email-address'), email, setEmail, {
                            type: 'email',
                            autoComplete: 'email',
                            inputMode: 'email',
                            maxLength: 175
                        })}
                        <div className="flex gap-12">
                            {field('firstName', t('payment.email-input.first-name'), firstName, setFirstName, {
                                type: 'text',
                                autoComplete: 'given-name',
                                maxLength: 100
                            })}
                            {field('lastName', t('payment.email-input.last-name'), lastName, setLastName, {
                                type: 'text',
                                autoComplete: 'family-name',
                                maxLength: 100,
                                enterKeyHint: 'go',
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') confirmSheet();
                                }
                            })}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

const BUTTON_CLASS_NAME = cn(
    'flex min-h-48 flex-1 items-center justify-center rounded-4 px-16 text-button-16 outline-none',
    'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2'
);

/** The checkout's answer when a payment went to its provider and stopped there (CIN-15446). */
function StalledPayment({
    provider,
    onContinue,
    onChooseAnother
}: {
    provider: string | null;
    onContinue: () => void;
    onChooseAnother: () => void;
}): React.JSX.Element {
    const t = useTranslations('tickets');
    return (
        <div
            role="alert"
            className="flex flex-col space-y-12 rounded-4 border border-[var(--primary-color)] p-16">
            <p className="text-body-bold-14">
                {provider
                    ? t('checkout.redirect-stalled-title', { method: provider })
                    : t('checkout.redirect-stalled-title-generic')}
            </p>
            <p className="text-body-14">{t('checkout.redirect-stalled-message')}</p>
            <div className="flex flex-col gap-8 lg:flex-row">
                <button
                    type="button"
                    onClick={onContinue}
                    className={cn(BUTTON_CLASS_NAME, 'bg-[var(--primary-color)] text-[var(--on-primary-color)]')}>
                    {provider ? t('checkout.continue-to', { method: provider }) : t('checkout.continue-payment')}
                </button>
                <button
                    type="button"
                    onClick={onChooseAnother}
                    className={cn(BUTTON_CLASS_NAME, 'border border-[var(--primary-color)]')}>
                    {t('checkout.choose-another-method')}
                </button>
            </div>
        </div>
    );
}
